/**
 * StructureSyncPanel — Tools › Structure Synchronization (admin), a top-level tab.
 *
 * Diff ONE source-of-truth schema (connection + database) against N targets (the
 * fleet matrix), preview the generated DDL per target, and apply it as a single
 * sequential background job (per-statement progress + skip/skip-all/abort via the
 * shared ProgressModal).
 *
 * Object statuses per cell: same / different / missing (create) / extra (drop,
 * mirror only). A ⚠ marks a cell whose statements carry a data-loss warning.
 */
import { createSignal, createResource, createMemo, createEffect, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { ssDiff, ssPreview, ssApply, ssResolve } from '../lib/structuresync.js';
import CodeMirrorSQL from '../components/CodeMirrorSQL.jsx';
import ProgressModal from '../components/ProgressModal.jsx';

const STATUS = {
  same:      { glyph: '✓', label: 'same',      cls: 'ss-same' },
  different: { glyph: '~', label: 'different', cls: 'ss-diff' },
  missing:   { glyph: '+', label: 'missing',   cls: 'ss-missing' },
  extra:     { glyph: '−', label: 'extra',     cls: 'ss-extra' },
};

export default function StructureSyncPanel(props) {
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; }
    catch { return []; }
  });

  // ── Source ─────────────────────────────────────────────────────────────────
  const [sourceConnId, setSourceConnId] = createSignal(0);
  const [sourceDb, setSourceDb] = createSignal('');
  const [sourceDbs] = createResource(sourceConnId, async (id) => {
    if (!id) return [];
    try { return (await api.get(`/api/connections/${id}/databases`)).databases || []; }
    catch { return []; }
  });

  // ── Targets ────────────────────────────────────────────────────────────────
  // Per-connection selection: { [connId]: { checked, db } }. db defaults to the
  // source db name (the fleet norm) and is editable per target. The SOURCE
  // connection is a valid target too (sync dbA → dbB on one server) — its db
  // deliberately defaults to EMPTY so the different database is an explicit
  // pick; only the exact same (connection, database) pair is refused.
  const [sel, setSel] = createStore({});
  const targetConns = createMemo(() => conns() || []);
  const isSource = (id) => id === sourceConnId();
  const defaultDb = (id) => (isSource(id) ? '' : (sourceDb() || ''));
  const groups = createMemo(() => {
    const g = new Set();
    for (const c of targetConns()) if (c.group) g.add(c.group);
    return [...g].sort();
  });
  // Per-connection database lists for the target db pickers, fetched lazily on
  // first check. false = loading, null = load failed (offline → free-text
  // fallback), array of names = loaded.
  const [dbLists, setDbLists] = createStore({});
  const ensureDbList = (id) => {
    if (dbLists[id] !== undefined) return;
    setDbLists(id, false);
    api.get(`/api/connections/${id}/databases`)
      .then((r) => setDbLists(id, (r.databases || []).map((d) => d.name)))
      .catch(() => setDbLists(id, null));
  };
  const toggleTarget = (id, on) => {
    if (on) ensureDbList(id);
    setSel(id, (prev) => ({ checked: on, db: (prev?.db || defaultDb(id)) }));
  };
  const selectGroup = (group) => {
    for (const c of targetConns()) {
      if (c.group === group) {
        ensureDbList(c.id);
        setSel(c.id, { checked: true, db: sel[c.id]?.db || defaultDb(c.id) });
      }
    }
  };
  // setSel({}) would MERGE (a no-op on a non-empty store) — reconcile replaces.
  const clearTargets = () => setSel(reconcile({}));
  const chosenTargets = createMemo(() =>
    targetConns()
      .filter((c) => sel[c.id]?.checked && (sel[c.id]?.db || '').trim())
      .map((c) => ({ connId: c.id, name: c.name, db: sel[c.id].db.trim() })));

  const [mirror, setMirror] = createSignal(false);

  // ── Diff ───────────────────────────────────────────────────────────────────
  // diffData carries `req` — the source + Mirror flag it was computed with —
  // and preview/apply use THAT, never the live pickers, so a script can't drop
  // objects the matrix never showed. Changing an input invalidates the diff
  // (diffGen also discards a diff still in flight).
  const [diffData, setDiffData] = createSignal(null);
  const [diffing, setDiffing] = createSignal(false);
  let diffGen = 0;
  const invalidateDiff = () => { diffGen++; setDiffData(null); setPreview(null); };
  const runDiff = async () => {
    if (!sourceConnId() || !sourceDb()) { toast.error('Pick a source connection + database.'); return; }
    const targets = chosenTargets();
    if (targets.length === 0) { toast.error('Pick at least one target (with a database).'); return; }
    if (targets.some((t) => t.connId === sourceConnId() && t.db.toLowerCase() === sourceDb().toLowerCase())) {
      toast.error('A target can’t be the same connection AND database as the source.');
      return;
    }
    const gen = ++diffGen;
    const req = { sourceConnId: sourceConnId(), sourceDb: sourceDb(), mirror: mirror() };
    setDiffing(true);
    setDiffData(null);
    setPreview(null);
    try {
      const r = await ssDiff({ ...req, targets: targets.map((t) => ({ connId: t.connId, db: t.db })) });
      if (gen === diffGen) setDiffData({ ...r, req });
    } catch (e) { if (gen === diffGen) toast.error('Diff failed: ' + e.message); }
    finally { setDiffing(false); }
  };

  // Union of objects across all targets → matrix rows (kind:name), sorted.
  const matrixRows = createMemo(() => {
    const d = diffData();
    if (!d) return [];
    const seen = new Map();
    for (const t of d.targets || []) {
      for (const o of t.objects || []) {
        const key = `${o.kind}:${o.name.toLowerCase()}`;
        if (!seen.has(key)) seen.set(key, { kind: o.kind, name: o.name });
      }
    }
    const kindOrder = { table: 0, view: 1, procedure: 2, function: 3, event: 4 };
    return [...seen.values()].sort((a, b) =>
      (kindOrder[a.kind] - kindOrder[b.kind]) || a.name.localeCompare(b.name));
  });
  // Per-target lookup: "kind:name" → object summary.
  const cellIndex = createMemo(() => {
    const d = diffData();
    const idx = {};
    for (const t of d?.targets || []) {
      const m = {};
      for (const o of t.objects || []) m[`${o.kind}:${o.name.toLowerCase()}`] = o;
      idx[t.connId] = m;
    }
    return idx;
  });
  const targetChanges = (t) => (t.counts?.different || 0) + (t.counts?.missing || 0) + (t.counts?.extra || 0);

  // ── Preview + apply ──────────────────────────────────────────────────────────
  const [preview, setPreview] = createSignal(null);   // { connId, name, db, script, violations }
  const [previewBusy, setPreviewBusy] = createSignal(false);
  const loadPreview = async (t) => {
    const d = diffData();
    if (!d) return;
    const gen = diffGen;
    setPreviewBusy(true);
    try {
      const r = await ssPreview({ ...d.req, target: { connId: t.connId, db: t.db } });
      if (gen === diffGen) setPreview({ connId: t.connId, name: t.name, db: t.db, script: r.script || '', violations: r.violations || [] });
    } catch (e) { toast.error('Preview failed: ' + e.message); }
    finally { setPreviewBusy(false); }
  };

  const [applyToken, setApplyToken] = createSignal(null);
  const apply = async (targets) => {
    const usable = targets.filter((t) => (t.script || '').trim());
    if (usable.length === 0) { toast.error('Nothing to apply — these targets are already in sync.'); return; }
    if (!confirm(`Apply structure changes to ${usable.length} target${usable.length === 1 ? '' : 's'}? This runs ALTER/CREATE/DROP DDL.`)) return;
    try {
      const r = await ssApply({ targets: usable.map((t) => ({ connId: t.connId, db: t.db, script: t.script })) });
      setApplyToken(r.token);
    } catch (e) { toast.error('Apply failed: ' + e.message); }
  };
  const applyOne = () => { const p = preview(); if (p) apply([p]); };
  const applyAll = async () => {
    const d = diffData();
    const targets = (d?.targets || []).filter((t) => t.reachable && targetChanges(t) > 0);
    if (targets.length === 0) { toast.error('No targets have changes to apply.'); return; }
    const gen = diffGen;
    setPreviewBusy(true);
    try {
      const scripts = await Promise.all(targets.map(async (t) => {
        const r = await ssPreview({ ...d.req, target: { connId: t.connId, db: t.db } });
        return { connId: t.connId, name: t.name, db: t.db, script: r.script || '' };
      }));
      if (gen !== diffGen) { toast.error('The diff changed while preparing — review it and apply again.'); return; }
      await apply(scripts);
    } catch (e) { toast.error('Apply-all failed: ' + e.message); }
    finally { setPreviewBusy(false); }
  };

  // Default the source db to the connection's first db once they load.
  createEffect(() => {
    if (!sourceDb() && (sourceDbs() || []).length) setSourceDb(sourceDbs()[0].name);
  });

  return (
    <div class="ssync-panel">
      <div class="ssync-head">
        <h2>🔀 Structure Synchronization</h2>
        <p class="muted" style="font-size:12px;margin:2px 0 0;">
          Diff one source-of-truth schema against many targets, then bring each in line.
          Tables get fine-grained ALTERs; views/routines/events are recreated. Mirror mode also drops target-only objects.
        </p>
      </div>

      {/* Source */}
      <div class="ssync-row">
        <label class="ssync-label">Source</label>
        <select class="input" style="max-width:240px;" value={sourceConnId()}
                onChange={(e) => { setSourceConnId(Number(e.target.value)); setSourceDb(''); invalidateDiff(); }}>
          <option value="0">— connection —</option>
          <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
        </select>
        <select class="input" style="max-width:220px;" value={sourceDb()} disabled={!sourceConnId()}
                onChange={(e) => { setSourceDb(e.target.value); invalidateDiff(); }}>
          <option value="">— database —</option>
          <For each={sourceDbs() || []}>{(db) => <option value={db.name}>{db.name}</option>}</For>
        </select>
        <label class="checkbox-label" title="Also DROP objects that exist on a target but not the source (exact mirror). Carries the strongest data-loss warnings.">
          <input type="checkbox" checked={mirror()} onChange={(e) => { setMirror(e.target.checked); invalidateDiff(); }} />
          Mirror (drop target-only)
        </label>
      </div>

      {/* Targets */}
      <div class="ssync-row ssync-targets-row">
        <label class="ssync-label">Targets</label>
        <div class="ssync-targets">
          <Show when={groups().length}>
            <div class="ssync-groups">
              <span class="muted" style="font-size:11px;">Quick add:</span>
              <For each={groups()}>
                {(g) => <button class="btn btn-sm btn-ghost" onClick={() => selectGroup(g)}>＋ {g}</button>}
              </For>
              <button class="btn btn-sm btn-ghost" onClick={clearTargets}>Clear</button>
            </div>
          </Show>
          <div class="ssync-target-list">
            <For each={targetConns()}>
              {(c) => (
                <div class={`ssync-target ${sel[c.id]?.checked ? 'on' : ''}`}>
                  <label class="checkbox-label">
                    <input type="checkbox" checked={!!sel[c.id]?.checked}
                           onChange={(e) => toggleTarget(c.id, e.target.checked)} />
                    <span>{c.name}</span>
                    <Show when={isSource(c.id)}><span class="ssync-grp" title="The source connection — pick a different database to sync within one server">source conn</span></Show>
                    <Show when={c.group && !isSource(c.id)}><span class="ssync-grp">{c.group}</span></Show>
                  </label>
                  <Show when={sel[c.id]?.checked}>
                    {/* Combo picker: a datalist dropdown of the server's databases
                        that still accepts free text — apply supports a NEW db name
                        (CREATE DATABASE IF NOT EXISTS), so typing must stay possible.
                        Load failure (offline) just means no suggestions. */}
                    <input class="input mono ssync-db" list={`ssync-dbs-${c.id}`}
                           placeholder={
                             dbLists[c.id] === false ? 'database (loading list…)'
                             : dbLists[c.id] === null ? 'database (list unavailable)'
                             : isSource(c.id) ? 'a different database' : 'database'
                           }
                           value={sel[c.id]?.db || ''}
                           onInput={(e) => setSel(c.id, 'db', e.target.value)} />
                    <datalist id={`ssync-dbs-${c.id}`}>
                      <For each={dbLists[c.id] || []}>
                        {(db) => (
                          <Show when={!(isSource(c.id) && db.toLowerCase() === sourceDb().toLowerCase())}>
                            <option value={db} />
                          </Show>
                        )}
                      </For>
                    </datalist>
                  </Show>
                </div>
              )}
            </For>
            <Show when={(targetConns() || []).length === 0}>
              <div class="muted" style="font-size:12px;">No connections to target.</div>
            </Show>
          </div>
        </div>
      </div>

      <div class="ssync-actions">
        <button class="btn" onClick={runDiff} disabled={diffing()}>
          {diffing() ? 'Diffing…' : '⇄ Diff fleet'}
        </button>
        <Show when={diffData()}>
          <button class="btn btn-ghost" onClick={applyAll} disabled={previewBusy()}>▶▶ Apply all targets…</button>
        </Show>
        <span class="spacer" />
        <Show when={diffData()}>
          <span class="ssync-legend">
            <span class="ss-badge ss-same">✓ same</span>
            <span class="ss-badge ss-diff">~ different</span>
            <span class="ss-badge ss-missing">+ missing</span>
            <span class="ss-badge ss-extra">− extra</span>
          </span>
        </Show>
      </div>

      {/* Matrix */}
      <Show when={diffData()}>
        <div class="ssync-matrix-wrap">
          <table class="ssync-matrix mono">
            <thead>
              <tr>
                <th class="ssync-objcol">Object</th>
                <For each={diffData().targets}>
                  {(t) => (
                    <th class={t.reachable ? '' : 'ssync-unreach'} title={t.error || t.db}>
                      <div class="ssync-thname">{t.name}</div>
                      <div class="ssync-thdb">{t.db}</div>
                      <Show when={!t.reachable}><div class="ssync-therr">offline</div></Show>
                      <Show when={t.missingDb}><div class="ssync-therr">db missing</div></Show>
                      <Show when={t.reachable}>
                        <div class="ssync-thcount">{targetChanges(t) === 0 ? 'in sync' : `${targetChanges(t)} chg`}</div>
                        <Show when={t.violations > 0}>
                          <div class="ssync-thviolation" title={`${t.violations} construct${t.violations === 1 ? '' : 's'} this server's version can't support — see Preview`}>
                            ⛔ {t.violations} incompatible
                          </div>
                        </Show>
                        <Show when={targetChanges(t) > 0}>
                          <button class="btn btn-xs" onClick={() => loadPreview({ connId: t.connId, name: t.name, db: t.db })}>Preview</button>
                        </Show>
                      </Show>
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={matrixRows()}>
                {(row) => (
                  <tr>
                    <td class="ssync-objcell"><span class="ssync-kind">{row.kind}</span> {row.name}</td>
                    <For each={diffData().targets}>
                      {(t) => {
                        const o = cellIndex()[t.connId]?.[`${row.kind}:${row.name.toLowerCase()}`];
                        const st = o ? STATUS[o.status] : null;
                        return (
                          <td class={`ssync-cell ${st ? st.cls : ''}`}
                              title={o ? `${o.status}${o.steps ? ` · ${o.steps} stmt` : ''}${o.warnings ? ` · ${o.warnings} ⚠ data-loss` : ''}${o.violations ? ` · ${o.violations} ⛔ version-incompatible` : ''}` : ''}>
                            <Show when={st} fallback={<span class="muted">·</span>}>
                              {st.glyph}{o.warnings > 0 ? ' ⚠' : ''}{o.violations > 0 ? ' ⛔' : ''}
                            </Show>
                          </td>
                        );
                      }}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>

      {/* Per-target preview + apply */}
      <Show when={preview()}>
        <div class="ssync-preview">
          <div class="ssync-preview-head">
            <strong>Preview — {preview().name} · {preview().db}</strong>
            <span class="spacer" />
            <button class="btn btn-sm btn-danger" onClick={applyOne}>▶ Apply to this target</button>
            <button class="btn btn-sm btn-ghost" onClick={() => setPreview(null)}>Close</button>
          </div>
          <Show when={(preview().violations || []).length > 0}>
            <div class="ssync-preview-violations">
              ⛔ This target's server version can't support {preview().violations.length === 1 ? 'this' : 'these'} — review before applying:
              <ul>
                <For each={preview().violations}>
                  {(v) => <li>{v.kind} <code>{v.name}</code>: {v.reason}</li>}
                </For>
              </ul>
            </div>
          </Show>
          <Show when={(preview().script || '').trim()} fallback={<div class="muted" style="padding:12px;">Already in sync — no statements.</div>}>
            <CodeMirrorSQL value={preview().script} onInput={(v) => setPreview({ ...preview(), script: v })} height="260px" />
          </Show>
        </div>
      </Show>

      {/* Apply progress */}
      <Show when={applyToken()}>
        <ProgressModal
          token={applyToken()}
          title="Applying structure sync"
          kind="restore"
          onResolve={(action) => ssResolve(applyToken(), action)}
          onClose={() => { setApplyToken(null); runDiff(); }}
        />
      </Show>
    </div>
  );
}
